import { logError } from '../lib/logger';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ChevronRight } from 'lucide-react';
import ContactBrandIcon from '../components/ContactBrandIcon';
import { contactHref, contactDisplayValue } from '../lib/contactHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXTERNAL_KINDS = new Set(['web', 'instagram', 'whatsapp', 'telegram']);

const KIND_META = {
  phone:     { tint: 'bg-emerald-50', text: 'text-emerald-700', label: 'Chiama' },
  email:     { tint: 'bg-blue-50',    text: 'text-blue-700',    label: 'Scrivi' },
  whatsapp:  { tint: 'bg-green-50',   text: 'text-green-700',   label: 'WhatsApp' },
  instagram: { tint: 'bg-pink-50',    text: 'text-pink-700',    label: 'Apri profilo' },
  telegram:  { tint: 'bg-sky-50',     text: 'text-sky-700',     label: 'Telegram' },
  web:       { tint: 'bg-zinc-100',   text: 'text-zinc-700',    label: 'Apri' },
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    setLoadError('');
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts`);
      setContacts(res.data);
    } catch (err) {
      logError('Contacts', err);
      setLoadError('Impossibile caricare i contatti. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <main className="max-w-2xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8 md:mb-10">
          <h1 className="text-3xl md:text-4xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Contatti
          </h1>
          <p className="text-zinc-500 text-sm md:text-base">Tocca un contatto per chiamare, scrivere o aprire il link.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>
        ) : loadError ? (
          <div className="text-center py-16" role="alert">
            <p className="text-red-700 text-sm mb-4">{loadError}</p>
            <button
              type="button"
              onClick={load}
              className="px-4 py-2 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] text-white text-sm font-medium"
            >
              Riprova
            </button>
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-16 text-zinc-400 text-sm">Nessun contatto disponibile</div>
        ) : (
          <ul className="space-y-3">
            {contacts.map((c) => {
              const meta = KIND_META[c.kind] || KIND_META.web;
              const href = contactHref(c);
              const display = contactDisplayValue(c);
              return (
                <li key={c.id}>
                  <a
                    href={href}
                    target={EXTERNAL_KINDS.has(c.kind) ? '_blank' : undefined}
                    rel={EXTERNAL_KINDS.has(c.kind) ? 'noopener noreferrer' : undefined}
                    className="group flex items-center gap-4 p-4 bg-white border border-zinc-200 rounded-2xl hover:border-[#D4AF37] hover:shadow-md transition-all duration-200 active:scale-[0.99]"
                    data-testid={`contact-${c.id}`}
                  >
                    <ContactBrandIcon kind={c.kind} size={48} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900 leading-tight truncate">{c.label}</p>
                      <p className="text-sm text-zinc-500 mt-0.5 truncate" title={display}>{display}</p>
                    </div>
                    <div className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium ${meta.tint} ${meta.text}`}>
                      {meta.label}
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
                  </a>
                </li>
              );
            })}
          </ul>
        )}
    </main>
  );
};

export default Contacts;
